import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../utils/helper';
import { ButtonUI, DataGridIdentifiers, DatagridPremiumUi } from '../Interface';
import prodgeneratorPartialService from '../../service/prodgeneratorPartial.service';
import useUsers from '../../context/UsersContext';
import useConfig from '../../context/ConfigContext';
import DrillArrow from '../Interface/DrillArrow';
import SourceListDialog from '../SourceList/SourceListDialog';
import DetailDialogPartial from './DetailDialogPartial';
import { Button } from '@mui/material';
import { multiply } from 'mathjs';
import {
  getProductionQuantityForOrder,
  getReservedQuantity,
  shouldDisplayOrder,
} from './productionGeneratorPartial.utils';

const ProductionGeneratorPartialTable = ({ refetchState, selectedOrderState, getTreeData, getProdsAndFromWhs }) => {
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();

  const { selectedOrder, setSelectedOrder } = selectedOrderState;
  const { refetchTable, setRefetchTable } = refetchState;
  const [loading, setLoading] = useState(true);
  const [selectedItemCode, setSelectedItemCode] = useState(null);
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [details, setDetails] = useState({
    fromWarehouse: [],
    production: [],
  });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'dueDate', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 100,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length) {
      setLoading(false);
    }
  }, [user]);

  const getList = (gridState) => {
    prodgeneratorPartialService.getOrders(gridState).then((res) => {
      const filteredRows = (res.rows ?? [])
        .map((row) => ({
          ...row,
          reservedQty: getReservedQuantity(row),
          productionQty: getProductionQuantityForOrder(row),
        }))
        .filter(shouldDisplayOrder);

      setReports({
        ...res,
        rows: filteredRows,
        rowCount: res.rowCount ?? filteredRows.length,
      });
    });
  };

  const columns = (sourceListDialogOpen) => {
    return dataGridPremiumColumns([
      {
        field: 'detailCol',
        headerName: '',
        type: 'actions',
        renderCell: (params) => {
          return (
            <ButtonUI text="Részletek" color="blue-gray" className="px-1" onClick={() => handleDetailClick(params)} />
          );
        },
      },
      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        valueGetter: (value) => new Date(value),
        valueFormatter: (value) => dayjs(value).format('YYYY.MM.DD.'),
      },
      {
        field: 'docNum',
        headerName: 'Bizonylatszám',
        type: 'string',
      },
      {
        field: 'companyCode',
        headerName: 'Cég',
        type: 'string',
      },
      {
        field: 'companyName',
        headerName: 'Cég neve',
        type: 'string',
      },
      {
        field: 'itemCode',
        headerName: 'Cikkszám',
        type: 'string',
        renderCell: (params) => {
          return (
            <div>
              {params.row.itemCode}

              <DrillArrow
                onClick={(e) => {
                  sourceListDialogOpen(params.row.itemCode);
                }}
              />
            </div>
          );
        },
      },
      {
        field: 'itemName',
        headerName: 'Cikknév',
        type: 'string',
      },
      {
        field: 'qty',
        headerName: 'Rendelt mennyiség',
        type: 'number',
      },
      {
        field: 'reservedQty',
        headerName: 'Foglalt mennyiség',
        type: 'number',
      },
      {
        field: 'productionQty',
        headerName: 'Gyártandó mennyiség',
        type: 'number',
      },
      {
        field: 'onHand',
        headerName: 'Készlet',
        type: 'number',
      },
      {
        field: 'quantityUnitCode',
        headerName: 'ME',
        type: 'string',
      },
      {
        field: 'reference',
        headerName: 'Rend hiv. szám',
        type: 'string',
      },
      {
        field: 'itemReference',
        headerName: 'Tétel hiv. szám',
        type: 'string',
      },
      {
        field: 'internalNote',
        headerName: 'Belső megjegyzés',
        type: 'string',
      },
      {
        field: 'customerNote',
        headerName: 'Vevői megjegyzés',
        type: 'string',
      },
      {
        field: 'qtyWeight',
        headerName: 'Rendelt mennyiség súlya',
        type: 'number',
        renderCell: (data) => {
          return data.row.qtyWeight ? multiply(data.row.qty, data.row.qtyWeight).toFixed(2) : '';
        },
      },
      {
        field: 'fixMe',
        headerName: 'ME2',
        type: 'string',
        filterable: false,
        sortable: false,
        renderCell: () => {
          return 'kg';
        },
      },
      {
        field: 'issueDate',
        headerName: 'Bizonylat kelte',
        type: 'date',
        valueGetter: (value) => new Date(value),
        valueFormatter: (value) => dayjs(value).format('YYYY.MM.DD.'),
      },
      {
        field: 'itemGroupName',
        headerName: 'Cikkcsoport',
        type: 'string',
      },
      {
        field: 'missingProdOrder',
        headerName: 'Részben átvett',
        type: 'boolean',
      },
      {
        field: 'hasProduction',
        headerName: 'Átvett rendelés',
        type: 'boolean',
      },
    ]);
  };

  const getRowClassName = (params) => {
    let classNames = '';

    if (params.row.missingTree || params.row.missingOperation) classNames += 'datagrid-line-missingTree ';
    if (params.row.missingSourceList) classNames += 'datagrid-line-missingSourceList ';
    if (params.row.missingProdOrder) classNames += 'datagrid-line-missingProdOrder ';
    if (params.row.hasProduction && !params.row.missingTree) classNames += 'datagrid-line-hasProduction ';

    return classNames.trim();
  };

  const handleDetailClick = (params) => {
    const { row } = params;

    setDetailDialogOpen(true);
    prodgeneratorPartialService.getDetails({ docId: row.docId, lineNum: row.lineNum }).then((data) => {
      setDetails(data);
    });
  };

  const sourceListDialogOpen = (itemCode) => {
    setSelectedItemCode(itemCode);
  };

  const sourceListDialogClose = () => {
    setSelectedItemCode(null);
  };

  const handleMarkAsPrepared = () => {
    prodgeneratorPartialService.markAsPrepared(selectedOrder.docId, selectedOrder.lineNum).then(() => {
      setRefetchTable(true);
    });
  };

  return (
    <>
      {!loading && (
        <DatagridPremiumUi
          datagridIdentifier={DataGridIdentifiers.ProdGeneratorPartialTable}
          datagridInitialState={initialState}
          dataLoader={getList}
          height={400}
          rows={reports.rows}
          refetch={{ refetchTable, setRefetchTable }}
          rowCount={reports.rowCount}
          columns={columns(sourceListDialogOpen)}
          paginationAndfilterMode="server"
          onRowClick={(params) => {
            setSelectedOrder(params.row);
            getTreeData(params.row.itemCode);
            getProdsAndFromWhs({
              docId: params.row.docId,
              lineNum: params.row.lineNum,
            });
          }}
          getRowId={(row) => row.generatedNum}
          getRowClassName={getRowClassName}
          disableRowSelectionOnClick
          customHeaderComponent={
            <Button
              variant="text"
              size="small"
              color="primary"
              disableElevation
              disabled={!Object.keys(selectedOrder).length}
              onClick={() => handleMarkAsPrepared()}
            >
              Teljesen kigyekeltnek jelölés
            </Button>
          }
        />
      )}
      <SourceListDialog
        showDialog={selectedItemCode}
        onClose={sourceListDialogClose}
        selectedItemCode={selectedItemCode}
      />

      <DetailDialogPartial open={detailDialogOpen} handleClose={() => setDetailDialogOpen(false)} data={details} />
    </>
  );
};

export default ProductionGeneratorPartialTable;
